import React, { useEffect, useRef, useState } from "react";
import { X, Search, TrendingUp, ArrowRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import client from "@/lib/api";

const POPULAR = ["Gold Ring", "Bridal Necklace", "Diamond Earrings", "Bangles", "Chandrahaar", "Guluband"];

export default function SearchOverlay({ open, onClose }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    const nav = useNavigate();

    useEffect(() => {
        if (open) setTimeout(() => inputRef.current?.focus(), 60);
        else { setQ(""); setResults([]); }
    }, [open]);

    useEffect(() => {
        if (!q || q.length < 2) { setResults([]); return; }
        setLoading(true);
        const t = setTimeout(() => {
            client.get(`/products?search=${encodeURIComponent(q)}&limit=8`).then(r => {
                setResults(r.data);
                setLoading(false);
            }).catch(() => setLoading(false));
        }, 220);
        return () => clearTimeout(t);
    }, [q]);

    if (!open) return null;

    const submit = (e) => {
        e.preventDefault();
        if (!q.trim()) return;
        onClose();
        nav(`/products?search=${encodeURIComponent(q)}`);
    };

    return (
        <div className="fixed inset-0 z-[60] bg-[color:var(--ivory)]/98 backdrop-blur-lg fade-in" data-testid="search-overlay">
            <div className="max-w-[1440px] mx-auto px-6 lg:px-10 pt-6 lg:pt-10 pb-10 h-full overflow-y-auto">
                <div className="flex items-center justify-between mb-10">
                    <div className="font-accent tracking-[0.32em] text-[color:var(--charcoal)]">SEARCH</div>
                    <button onClick={onClose} data-testid="search-close" aria-label="Close" className="text-[color:var(--charcoal)] hover:text-[color:var(--champagne)]"><X size={22} /></button>
                </div>

                <form onSubmit={submit} className="relative border-b-2 border-[color:var(--charcoal)] pb-3 mb-8">
                    <Search size={22} className="absolute left-0 top-1 text-[color:var(--muted-brown)]" />
                    <input
                        ref={inputRef}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search jewellery, code, category…"
                        className="w-full pl-9 pr-4 py-2 bg-transparent outline-none font-serif-luxe text-2xl md:text-4xl text-[color:var(--charcoal)] placeholder:text-[color:var(--muted-brown)]/50"
                        data-testid="search-overlay-input"
                    />
                </form>

                {!q && (
                    <div className="grid md:grid-cols-2 gap-10 mt-10">
                        <div>
                            <div className="eyebrow mb-4 flex items-center gap-2"><TrendingUp size={12} /> Popular</div>
                            <div className="flex flex-wrap gap-2">
                                {POPULAR.map(p => (
                                    <button key={p} onClick={() => setQ(p)} className="text-xs tracking-[0.15em] uppercase border border-[color:var(--soft-border)] hover:border-[color:var(--champagne)] hover:text-[color:var(--champagne)] px-4 py-2 transition-colors" data-testid={`popular-${p.toLowerCase().replace(/\s/g,'-')}`}>{p}</button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <div className="eyebrow mb-4">Quick Categories</div>
                            <ul className="space-y-2">
                                {[
                                    { name: "Gold Jewellery", path: "/gold-jewellery" },
                                    { name: "Silver Jewellery", path: "/silver-jewellery" },
                                    { name: "Diamond Jewellery", path: "/diamond-jewellery" },
                                    { name: "Bridal Collection", path: "/bridal-jewellery" },
                                    { name: "New Arrivals", path: "/new-arrivals" },
                                ].map(c => (
                                    <li key={c.name}><Link to={c.path} onClick={onClose} className="flex items-center justify-between py-2 border-b border-[color:var(--soft-border)] hover:text-[color:var(--champagne)]"><span>{c.name}</span><ArrowRight size={14} /></Link></li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}

                {q && q.length < 2 && <div className="text-sm text-[color:var(--muted-brown)]">Keep typing…</div>}

                {q && q.length >= 2 && (
                    <div>
                        <div className="eyebrow mb-5">{loading ? "Searching…" : `${results.length} result${results.length !== 1 ? "s" : ""}`}</div>
                        {results.length > 0 ? (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {results.map(p => (
                                    <Link key={p.id} to={`/products/${p.id}`} onClick={onClose} className="group" data-testid={`search-hit-${p.id}`}>
                                        <div className="product-img-wrap aspect-[4/5] mb-3">
                                            <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                            <img className="watermark" src="/logo-watermark.png" alt="" />
                                        </div>
                                        <div className="font-serif-luxe text-base">{p.name}</div>
                                        <div className="text-[11px] tracking-widest text-[color:var(--muted-brown)]">{p.sku}</div>
                                    </Link>
                                ))}
                            </div>
                        ) : !loading && (
                            <div className="py-16 text-center">
                                <div className="font-serif-luxe text-2xl mb-2">Nothing quite matches</div>
                                <div className="text-sm text-[color:var(--muted-brown)]">Try a different search, or explore our collections.</div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
